// «Пользователи» — как раздел «Клиенты» в «Непростоботе»: карточки с цифрами, поиск, вкладки, сортировка, фильтры
// и карточка на каждого: слева — кто он и как с ним связаться, справа — строки активности (как «кабинеты»).
import { useEffect, useRef, useState } from "react";
import { getJson } from "../api";
import { Icon } from "../components/Icon";
import {
  ago, KINDS, num, PROVIDER_NAMES, StatCard, userTitle, when,
  type AdminUser, type Detail, type Filter, type Provider, type Sort, type Stats, type UserList,
} from "./shared";

const FILTERS: [Filter, string][] = [
  ["all", "Все"], ["registered", "Зарегистрированные"], ["guests", "Гости"], ["day", "Активны за сутки"],
  ["week", "За 7 дней"], ["active", "С документами"],
];
const SORTS: [Sort, string][] = [["name", "А–Я"], ["created", "По дате регистрации"], ["seen", "По последнему визиту"]];
const PROVIDERS: [Provider, string][] = [["", "Любой"], ["google", "Google"], ["yandex", "Яндекс ID"], ["email", "Код на почту"], ["none", "Без входа"]];

// Строки справа в карточке — как «кабинеты» клиента: значок, название, отметка, когда последний раз, число.
function ActivityRow({ icon, tone, title, count, last, note }: {
  icon: string; tone: string; title: string; count: number; last: string | null; note?: string;
}) {
  return (
    <li className="adm-row">
      <span className={`adm-row-icon is-${tone}`}><Icon name={icon} /></span>
      <span className="adm-row-body">
        <span className="adm-row-title">
          {title}
          <span className={`adm-pill ${count ? "is-green" : "is-blue"}`}>{count ? "Есть" : "Пока нет"}</span>
        </span>
        <span className="adm-row-sub">{note || (last ? `последний раз — ${when(last)}` : "ни разу")}</span>
      </span>
      <span className="adm-row-count">{num(count)}</span>
    </li>
  );
}

function UserCard({ user, index, onLogs }: { user: AdminUser; index: number; onLogs: (user: AdminUser) => void }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!open || detail) return;
    getJson<Detail>(`/api/admin/users/${user.id}`).then(setDetail).catch((failure) => setError((failure as Error).message));
  }, [open, detail, user.id]);
  const registered = user.kind === "registered";

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(user.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* буфер обмена недоступен — почта видна на карточке */ }
  }

  return (
    <li className="adm-card adm-user" data-user={user.id}>
      <div className="adm-user-main">
        <div className="adm-user-head">
          <span className="adm-index">{index}</span>
          <h3>{userTitle(user)}</h3>
          {user.providers.map((provider) => <span key={provider} className="adm-pill is-green">{PROVIDER_NAMES[provider] || provider}</span>)}
          <span className={`adm-status ${registered ? "is-on" : ""}`}>{registered ? "Зарегистрирован" : "Гость"}<Icon name="updown" /></span>
        </div>
        <p className="adm-place"><Icon name="globe" />{[user.last_ip, user.browser].filter(Boolean).join(" · ") || "адрес неизвестен"}</p>
        {user.email && (
          <div className="adm-contact">
            <a className="adm-btn is-mail" href={`mailto:${user.email}`}><Icon name="mail" />Написать · Почта</a>
            <button type="button" className="adm-btn is-copy" onClick={copyEmail}><Icon name="copy" />{copied ? "Скопировано" : "Скопировать почту"}</button>
          </div>
        )}
        <dl className="adm-facts">
          <div><dt>Почта</dt><dd>{user.email || "—"}</dd></div>
          <div><dt>Аккаунт №</dt><dd>{user.id}</dd></div>
          <div><dt>Регистрация</dt><dd>{when(user.created_at)}</dd></div>
          <div><dt>Последний визит</dt><dd title={when(user.last_seen_at)}>{ago(user.last_seen_at)}</dd></div>
        </dl>
        <button type="button" className="adm-btn is-wide" aria-expanded={open} onClick={() => setOpen(!open)}>
          <Icon name="lines" />{open ? "Скрыть подробности" : "Подробнее о пользователе"}
        </button>
        {open && (
          <div className="adm-detail">
            {error ? <p className="adm-error">{error}</p> : !detail ? <p className="adm-muted">Загружаю…</p> : (
              <>
                <h4>Входы</h4>
                {detail.logins.length ? (
                  <ul className="adm-list">{detail.logins.map((login) => (
                    <li key={login.provider}><strong>{PROVIDER_NAMES[login.provider] || login.provider}</strong> {login.email || "—"} · с {when(login.created_at)}</li>
                  ))}</ul>
                ) : <p className="adm-muted">Не входил — гостевой аккаунт этого браузера.</p>}
                <p className="adm-muted">Открытых сессий: {detail.sessions}. {detail.user_agent}</p>
                <h4>Последнее</h4>
                {detail.recent.length ? (
                  <ul className="adm-list">{detail.recent.map((item, i) => (
                    <li key={i}><span className="adm-pill is-gray">{KINDS[item.kind] || item.kind}</span>{item.title} · {when(item.at)}</li>
                  ))}</ul>
                ) : <p className="adm-muted">Пока ничего не делал.</p>}
                <h4>Помощник по дням</h4>
                {detail.usage.length ? (
                  <ul className="adm-list">{detail.usage.map((day) => (
                    <li key={day.day}>{new Date(day.day).toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}: {day.assistant} сообщ. помощнику · {day.documents} вопр. мастера</li>
                  ))}</ul>
                ) : <p className="adm-muted">К помощнику не обращался.</p>}
              </>
            )}
          </div>
        )}
      </div>
      <div className="adm-user-side">
        <div className="adm-side-head">
          <span>Активность</span>
          <button type="button" className="adm-chip-link" onClick={() => onLogs(user)}><Icon name="external" />Логи</button>
        </div>
        <ul className="adm-rows">
          <ActivityRow icon="chat" tone="violet" title="Чаты" count={user.chats} last={user.last_chat} />
          <ActivityRow icon="doc" tone="blue" title="Документы" count={user.docs} last={user.last_doc} />
          <ActivityRow icon="calc" tone="amber" title="Расчёты госпошлины" count={user.calcs} last={user.last_calc} />
          <ActivityRow icon="upload" tone="teal" title="Загрузки" count={user.uploads} last={user.last_upload} />
          <ActivityRow icon="spark" tone="sky" title="Помощник" count={user.assistant_total} last={user.last_assistant}
            note={`сегодня — ${user.assistant_today}, всего — ${user.assistant_total}`} />
          <ActivityRow icon="folder" tone="gray" title="Папки" count={user.folders} last={null} note={user.folders ? "разложил документы" : "не создавал"} />
        </ul>
      </div>
    </li>
  );
}

export function UsersSection({ onLogs }: { onLogs: (user: AdminUser) => void }) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [sort, setSort] = useState<Sort>("created");
  const [provider, setProvider] = useState<Provider>("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [asked, setAsked] = useState("");              // поиск уходит на сервер через 300 мс после последней буквы
  const [list, setList] = useState<UserList | null>(null);
  const [error, setError] = useState("");
  const [more, setMore] = useState(false);
  const [round, setRound] = useState(0);               // «Обновить» — заново и цифры, и список
  const request = useRef(0);

  useEffect(() => { getJson<Stats>("/api/admin/stats").then(setStats).catch((failure) => setError((failure as Error).message)); }, [round]);
  useEffect(() => {
    const timer = setTimeout(() => setAsked(query.trim()), 300);
    return () => clearTimeout(timer);
  }, [query]);
  const params = (offset = 0) => new URLSearchParams({ filter, sort, q: asked, provider, offset: String(offset) });
  useEffect(() => {
    const number = ++request.current;
    getJson<UserList>(`/api/admin/users?${params()}`)
      .then((found) => { if (number === request.current) { setList(found); setError(""); } })
      .catch((failure) => setError((failure as Error).message));
  }, [filter, sort, asked, provider, round]);

  async function showMore() {
    if (!list) return;
    setMore(true);
    try {
      const next = await getJson<UserList>(`/api/admin/users?${params(list.items.length)}`);
      setList({ ...next, items: [...list.items, ...next.items] });
    } catch (failure) {
      setError((failure as Error).message);
    } finally {
      setMore(false);
    }
  }

  const all = stats ? stats.registered + stats.guests : 0;
  return (
    <>
      <ul className="adm-stats" aria-label="Сводка">
        <StatCard icon="userplus" tone="red" label="Новые за сутки" value={stats ? num(stats.new_day) : "…"}
          hint={stats ? `с аккаунтом — ${stats.registered_new_day}` : ""} />
        <StatCard icon="calendar" tone="gray" label="Активны сегодня" value={stats ? num(stats.active_day) : "…"}
          hint={stats ? `с аккаунтом — ${stats.registered_day}` : ""} />
        <StatCard icon="spark" tone="amber" label="ИИ сегодня" value={stats ? num(stats.assistant_today) : "…"}
          hint={stats ? `сообщений · мастер — ${stats.documents_today}` : ""} />
        <StatCard icon="check" tone="green" label={<>Аккаунтов · {stats ? num(stats.registered) : "…"}</>}
          value={stats ? `${num(stats.registered)} / ${num(all)}` : "…"} hint="с аккаунтом / всего"
          share={stats && all ? stats.registered / all : 0} />
      </ul>

      <div className="adm-title">
        <h1>Пользователи</h1>
        <button type="button" className="adm-btn is-primary" onClick={() => setRound(round + 1)}><Icon name="refresh" />Обновить</button>
      </div>

      <label className="adm-card adm-search">
        <Icon name="search" />
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)}
          placeholder="Имя, почта, IP или № аккаунта" aria-label="Поиск пользователей" />
        {query && <button type="button" className="adm-icon-btn" aria-label="Очистить поиск" onClick={() => setQuery("")}><Icon name="close" /></button>}
      </label>

      <div className="adm-chips" role="tablist" aria-label="Кого показать">
        {FILTERS.map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={filter === value}
            className={`adm-chip ${filter === value ? "is-active" : ""}`} onClick={() => setFilter(value)}>
            {label} <span>{list ? list.counts[value] : ""}</span>
          </button>
        ))}
      </div>

      <div className="adm-sort">
        <span>Сортировка</span>
        <div className="adm-segment" role="group" aria-label="Сортировка">
          {SORTS.map(([value, label]) => (
            <button key={value} type="button" aria-pressed={sort === value} className={sort === value ? "is-active" : ""}
              onClick={() => setSort(value)}>{label}</button>
          ))}
        </div>
      </div>

      <section className="adm-card adm-filters">
        <button type="button" className="adm-filters-head" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(!filtersOpen)}>
          <Icon name="filters" /><span>Фильтры{provider ? ` · ${PROVIDERS.find(([value]) => value === provider)?.[1]}` : ""}</span><Icon name="chevron" />
        </button>
        {filtersOpen && (
          <div className="adm-filters-body">
            <span className="adm-label">Способ входа</span>
            <div className="adm-chips">
              {PROVIDERS.map(([value, label]) => (
                <button key={value || "any"} type="button" aria-pressed={provider === value}
                  className={`adm-chip ${provider === value ? "is-active" : ""}`} onClick={() => setProvider(value)}>{label}</button>
              ))}
            </div>
          </div>
        )}
      </section>

      {error && <p className="adm-error">{error}</p>}
      {!list ? <p className="adm-muted">Загружаю…</p> : list.items.length ? (
        <>
          <ul className="adm-users">
            {list.items.map((user, i) => <UserCard key={user.id} user={user} index={i + 1} onLogs={onLogs} />)}
          </ul>
          {list.items.length < list.total && (
            <button type="button" className="adm-btn is-more" disabled={more} onClick={showMore}>
              {more ? "Загружаю…" : `Показать ещё (${list.total - list.items.length})`}
            </button>
          )}
        </>
      ) : <p className="adm-card adm-empty">{asked ? "Никого не нашлось — проверьте запрос." : "Здесь пока никого."}</p>}
    </>
  );
}
